import { useListAdminStandardDocumentsQuery } from 'API/order/documents/DocumentApi';
import { useField } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import { fieldName } from './DocumentSelector.fieldName';
import { Dropdown } from '../../../../../tailwindComponents/dropdown/Dropdown';
import { AddDocumentToList } from './AddDocumentToList';
import { assertDefined } from '../../../../../utilities/Assertions';
import { DocumentDetails } from './DocumentDetails';
import { Spinner } from '../../../../../ui-toolkit';
import { useSelectCurrentOrderId } from 'selectors/order/selectCurrentOrder';
import { sortByCreatedAtDescending } from '../../SortByCreatedAt';
import {
  defaultNormalizedDocumentCategoriesData,
  useListDocumentCategoriesQuery,
} from '../../../../../API/documentCategories/DocumentCategoriesApi';

export interface IDocumentSelectorContainerProps {
  // This Selector always uses documentId
  baseName: string;
  remove: (index: number) => void;
  push: (documentId: any) => void;
}

export const DocumentSelectorContainer: React.FC<IDocumentSelectorContainerProps> = ({ baseName, remove, push }) => {
  const [{ value: documentIdList }, , { setTouched }] = useField<string[]>(`${baseName}.${fieldName}`);
  const orderId = useSelectCurrentOrderId();
  assertDefined(orderId);

  const { data = [], isLoading, refetch } = useListAdminStandardDocumentsQuery({ orderId });
  const { data: { activeCategories } = defaultNormalizedDocumentCategoriesData } = useListDocumentCategoriesQuery();

  const listOfDocumentsWeKnowOf = useMemo(
    () => data.filter((document) => documentIdList.includes(document.id)),
    [data, documentIdList]
  );

  const [pdfToPreview, setPdfToPreview] = useState<string | undefined>(listOfDocumentsWeKnowOf?.[0]?.fileName);

  const documentOptions = useMemo(
    () => listOfDocumentsWeKnowOf.toSorted(sortByCreatedAtDescending).map((document) => document.fileName),
    [listOfDocumentsWeKnowOf]
  );

  useEffect(() => {
    if (documentOptions.length > 0 && pdfToPreview === undefined) setPdfToPreview(documentOptions.at(0));
  }, [documentOptions, pdfToPreview]);

  const onRemoveDocumentById = (documentId: string) => {
    const documentIndex = documentIdList.indexOf(documentId);
    remove(documentIndex);
    setTouched(true);
    setPdfToPreview(undefined);
  };

  const onAddNewDocumentToDropdown = (documentId: any) => {
    push(documentId);
    setTouched(true);
    const newDoc = data?.find((doc) => doc.id === documentId);
    setPdfToPreview(newDoc?.fileName);
  };

  return (
    <>
      {(isLoading || !data || !activeCategories) && <Spinner />}
      {data && activeCategories && (
        <>
          <div className={'flex w-full flex-row justify-between'}>
            <div className={'pr-4 text-xl font-semibold'}>Documents</div>
            {listOfDocumentsWeKnowOf.length > 1 && (
              <div data-test-id={'dropdown'}>
                <Dropdown
                  onSelect={(document) => setPdfToPreview(document)}
                  options={documentOptions}
                  option={pdfToPreview ?? ''}
                  title={''}
                  // eslint-disable-next-line react/style-prop-object
                  style={'document'}
                />
              </div>
            )}
          </div>
          <div className={'w-full pl-1 pt-2'}>
            {listOfDocumentsWeKnowOf.map((t) => (
              <React.Fragment key={t.id}>
                {/* Our PDF preview doesn't support changing the PDF, so make sure they are different components. */}
                {pdfToPreview === t.fileName && (
                  <DocumentDetails
                    categories={activeCategories}
                    onClose={() => setPdfToPreview(undefined)}
                    onRemove={onRemoveDocumentById}
                    pdfPreviewData={t}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
          <div className={'w-full'}>
            <AddDocumentToList
              baseName={baseName}
              categories={activeCategories}
              data={data}
              documentIdsToExclude={listOfDocumentsWeKnowOf.map((item) => item.id)}
              orderId={orderId}
              push={onAddNewDocumentToDropdown}
              refetch={refetch}
            />
          </div>
        </>
      )}
    </>
  );
};
